import React, { useEffect, useState } from "react";

import { useCourseGroupsOptions, useTopicGroups } from "api/hooks";
import { TopicGroupApi, TopicGroupCourseGroupApi, topicgroupsApi } from "api/topicgroups/topicgroupsApi";
import {
  AdminResourceDeleteModal,
  AdminSearchInput,
  FiltersBackground,
  PageHeader,
  PageMain,
} from "components/molecules";
import { routes } from "consts";
import { useNavigate } from "react-router-dom";
import { useDeleteResourceActions } from "utils/store";
import { formatDate, minutesToTime } from "utils";
import { IDropdownOption, MultiSelect, PaziaButton } from "components/atoms";
import { TanTable, TanTableColumn, useFilters, usePagination, useSorting } from "components/molecules";

export const TopicGroupsPage = (): JSX.Element | null => {
  const { setDeleteResource } = useDeleteResourceActions();
  const [selectedTopicGroups, setSelectedTopicGroups] = useState<number[]>([]);
  const navigate = useNavigate();
  const { loading: loadingCourseGroups, options: courseGroupsOptions } = useCourseGroupsOptions();
  const { searchPhrase, filter, setSearchPhrase, changeFilter, getInitFilterValues } = useFilters("topicGroups");
  const { limit, onPaginationChange, skip, pagination } = usePagination();
  const { sorting, onSortingChange, fieldWithOrder } = useSorting();
  const { isFetching, isLoading, total, topicGroups, invalidate } = useTopicGroups(
    skip,
    limit,
    searchPhrase,
    filter,
    fieldWithOrder,
  );

  useEffect(() => {
    invalidate();
  }, [sorting, pagination, searchPhrase, filter]);

  const columns = React.useMemo<TanTableColumn<TopicGroupApi>[]>(() => cols, []);

  const onCourseGroupChange = (courseGroups: IDropdownOption[]) => {
    if (courseGroups.length < 1) return changeFilter("courseGroups", "");
    const courseGroupsIds = courseGroups.map((x) => `'${x.value}'`).join(",");
    changeFilter("courseGroups", `courseGroups in (${courseGroupsIds})`);
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Jesteś pewien że chcesz usunąć ${selectedTopicGroups.length} grup tematów?`)) {
      return;
    }

    try {
      await Promise.all(selectedTopicGroups.map((id) => topicgroupsApi.delete(id)));
      setSelectedTopicGroups([]);
      invalidate();
    } catch (error) {
      console.error("Failed to delete topic groups:", error);
      // Optionally, you could add some error handling or notification here
    }
  };

  return (
    <PageMain>
      <PageHeader
        button={{
          title: "Dodaj +",
          onClick: () => navigate(routes.TOPICS_GROUPS_ADD),
        }}
      >
        Grupy tematów
      </PageHeader>
      <FiltersBackground>
        <div className="flex flex-wrap items-center gap-2">
          <AdminSearchInput onSearch={(sp) => setSearchPhrase(sp)} className="mt-2 self-end" />
          <MultiSelect
            label="Grupa kursów"
            name="courseGroups"
            disabled={loadingCourseGroups}
            options={courseGroupsOptions}
            defaultValue={getInitFilterValues("courseGroups")}
            onChange={onCourseGroupChange}
            className="grow"
          />
        </div>
      </FiltersBackground>

      <div className="my-4 flex justify-end">
        <PaziaButton type="danger" disabled={selectedTopicGroups.length < 1} onClick={handleDeleteSelected}>
          Usuń zaznaczone ({selectedTopicGroups.length})
        </PaziaButton>
      </div>

      <TanTable
        data={topicGroups}
        columns={columns}
        total={total}
        loading={isLoading}
        fetching={isFetching}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        pagination={pagination}
        sorting={sorting}
        onDelete={(x) => setDeleteResource(x.id)}
        onRowClick={(x) => navigate(routes.TOPICS_GROUPS_EDIT(x.id))}
        // @ts-ignore TODO ASDSA
        onSelectionChange={setSelectedTopicGroups} // new prop for row selection
      />
      <AdminResourceDeleteModal
        title="Usuń grupę tematów"
        description="Jesteś pewien że chcesz usunąć grupę tematów?"
        onSuccessDelete={invalidate}
        deleteAction={topicgroupsApi.delete}
      />
    </PageMain>
  );
};

const cols: TanTableColumn<TopicGroupApi>[] = [
  {
    id: "id",
    hideOnMobile: true,
    accessorKey: "id",
    header: "ID",
    enableColumnFilter: false,
    size: 10,
    cell: (info) => info.getValue(),
  },
  {
    id: "name",
    accessorKey: "name",
    header: "Nazwa",
    // @ts-ignore ignore TODO
    size: "auto",
    enableColumnFilter: false,
  },
  {
    id: "courseGroups",
    hideOnMobile: true,
    accessorKey: "courseGroups",
    header: "Grupa kursów",
    enableColumnFilter: false,
    size: 300,
    cell: (info) => {
      const groups = info.getValue<TopicGroupCourseGroupApi[]>();
      return <span>{groups.map((x) => x.name).join(", ")}</span>;
    },
  },
  {
    id: "time",
    hideOnMobile: true,
    accessorKey: "time",
    header: "Czas",
    size: 40,
    enableColumnFilter: false,
    cell: (info) => minutesToTime(info.getValue<number>()) || <span className="flex justify-center">-</span>,
  },
  {
    id: "createdDate",
    hideOnMobile: true,
    accessorKey: "createdDate",
    header: "Data utworzenia",
    size: 200,
    cell: (info) => formatDate(info.getValue<Date>(), true, true),
    enableColumnFilter: false,
  },
];
